/* ---------------------------------------------------------------------------------------------------
   WISHLIST PAGE (sections/main-wishlist.liquid)
   ---------------------------------------------------------------------------------------------------
   Swym renders the list and the product view into the page content and owns every action on them. This
   element keeps the theme-owned parts of the design in step with it:

     1. `data-view` — loading | list | detail | empty — read off Swym's DOM, which assets/wishlist-page.css
        uses to swap the toolbar, empty state and Continue shopping between views;
     2. the item count in the toolbar;
     3. the toolbar's "Back to list" and "Clear list", which press Swym's own (hidden) controls so Swym's
        state, confirmation modal and sync all behave exactly as shipped;
     4. on each card, the theme's rating badge and a sharper thumbnail;
     5. in the product view, "Remove from wishlist".

   Swym is a React app that re-renders whenever it likes, so all of this is re-applied from one
   MutationObserver, and every node we add is marked so a pass never touches it twice.
   --------------------------------------------------------------------------------------------------- */

(function () {
  'use strict';

  if (customElements.get('wishlist-page')) return;

  /* Swym requests card images at _360x360. Four-up at the theme's width each image is ~310 CSS px, a
     620px box on a 2x screen showing a 360px file. The CDN serves any _WxH on demand. */
  const SOFT_SIZE = /_360x360(?=[._])/;
  const SHARP_SIZE = '_720x720';

  /* One request per product per page view, shared by every card that shows it. */
  const ratingRequests = new Map();

  function productHandle(url) {
    const match = /\/products\/([^/?#]+)/.exec(url || '');

    return match ? match[1] : null;
  }

  function fetchRating(handle) {
    if (!ratingRequests.has(handle)) {
      /* templates/product.wishlist-rating.liquid renders snippets/product-rating.liquid alone. The root
         keeps the request inside the shopper's market (/en-ca/ etc.). */
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

      ratingRequests.set(
        handle,
        fetch(root + 'products/' + encodeURIComponent(handle) + '?view=wishlist-rating', { credentials: 'same-origin' })
          .then(function (response) {
            return response.ok ? response.text() : '';
          })
          .then(function (html) {
            return html.trim();
          })
          .catch(function () {
            return '';
          })
      );
    }

    return ratingRequests.get(handle);
  }

  function waitFor(test, timeout) {
    return new Promise(function (resolve) {
      const started = Date.now();

      (function poll() {
        const found = test();

        if (found || Date.now() - started > timeout) return resolve(found || null);

        setTimeout(poll, 50);
      })();
    });
  }

  class WishlistPage extends HTMLElement {
    connectedCallback() {
      this.count = null;
      this.scheduled = false;

      this.observer = new MutationObserver(this.schedule.bind(this));
      this.observer.observe(this, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });

      this.addEventListener('click', this.onClick.bind(this));
      this.update();
    }

    disconnectedCallback() {
      this.observer.disconnect();
    }

    get swym() {
      return this.querySelector('#swym-wishlist-render-container');
    }

    schedule() {
      if (this.scheduled) return;

      this.scheduled = true;

      setTimeout(() => {
        this.scheduled = false;
        this.update();
      }, 0);
    }

    update() {
      const swym = this.swym;

      if (!swym) return;

      let view = 'loading';

      if (swym.querySelector('.swym-showing-product-detail')) {
        view = 'detail';
      } else if (swym.querySelector('.swym-empty-wishlist-container')) {
        view = 'empty';
        this.count = 0;
      } else if (swym.querySelector('.swym-wishlist-grid')) {
        view = 'list';
        this.count = swym.querySelectorAll('.swym-wishlist-grid > li').length;
      }

      /* The grid is not rendered in the product view; the count carries over from the list. */
      if (this.dataset.view !== view) this.dataset.view = view;

      this.renderCount();

      if (view === 'list') this.decorateCards(swym);
      if (view === 'detail') this.decorateDetail(swym);
    }

    renderCount() {
      const node = this.querySelector('[data-wishlist-count]');

      if (!node || this.count === null) return;

      const template = this.count === 1 ? this.dataset.countOne : this.dataset.countOther;
      const text = (template || '[count]').replace('[count]', this.count);

      if (node.textContent !== text) node.textContent = text;

      /* Tell the header indicator (assets/wishlist.js). Swym's "Clear list" fires no removal event at
         all, so without this the header dot stays lit over an empty list until the next page load. */
      if (this.announcedCount !== this.count) {
        this.announcedCount = this.count;
        document.dispatchEvent(new CustomEvent('wishlist:count', { detail: { count: this.count } }));
      }
    }

    decorateCards(swym) {
      swym.querySelectorAll('.swym-wishlist-item').forEach(function (card) {
        const image = card.querySelector('img.swym-wishlist-image');
        const src = image && image.getAttribute('src');

        if (src && SOFT_SIZE.test(src)) image.setAttribute('src', src.replace(SOFT_SIZE, SHARP_SIZE));

        if (card.querySelector('.wishlist-card-rating')) return;

        const link = card.querySelector('.swym-wishlist-image-wrapper');
        const handle = productHandle(link && link.getAttribute('href'));
        const anchor = card.querySelector('.swym-add-to-cart-btn');

        if (!handle || !anchor) return;

        const rating = document.createElement('div');

        rating.className = 'wishlist-card-rating';
        rating.dataset.loading = '';
        card.insertBefore(rating, anchor);

        fetchRating(handle).then(function (html) {
          rating.innerHTML = html;
          delete rating.dataset.loading;
        });
      });
    }

    decorateDetail(swym) {
      swym.querySelectorAll('.swym-wishlist-product-detail-text-container').forEach((info) => {
        if (info.querySelector('.wishlist-detail-remove')) return;

        const button = document.createElement('button');
        const label = document.createElement('span');

        button.type = 'button';
        button.className = 'wishlist-detail-remove';
        label.textContent = this.dataset.removeLabel || '';
        button.append(this.icon(this.dataset.closeIcon, 'wishlist-page__icon-desktop', 7.875), this.icon(this.dataset.closeIconMobile, 'wishlist-page__icon-mobile', 11), label);
        info.appendChild(button);
      });
    }

    icon(src, className, size) {
      const img = document.createElement('img');

      img.src = src;
      img.className = className;
      img.width = size;
      img.height = size;
      img.alt = '';

      return img;
    }

    onClick(event) {
      if (event.target.closest('[data-wishlist-back]')) return this.backToList();
      if (event.target.closest('[data-wishlist-clear]')) return this.clearList();

      const remove = event.target.closest('.wishlist-detail-remove');

      if (remove) {
        event.stopPropagation();
        this.removeFromDetail(remove);
      }
    }

    backToList() {
      const back = this.swym && this.swym.querySelector('.swym-back-btn');

      if (back) back.click();
      this.scrollIntoView({ block: 'start' });
    }

    /* Swym's "Clear List" lives in a kebab menu we hide; opening it and pressing the item brings up
       Swym's own "Are you sure?" modal, which does the clearing. */
    clearList() {
      const menu = this.swym && this.swym.querySelector('#swym-wishlist-context-menu');

      if (!menu) return;

      menu.click();

      waitFor(() => this.swym.querySelector('.swym-wishlist-context-menu-item.swym-delete'), 1000).then(function (item) {
        if (item) item.click();
      });
    }

    /* Swym's product view has no remove control, and removing through its JS API leaves the rendered
       page stale (the grid keeps showing the item until a reload). Its card × does refresh it, so go
       back to the list and press that card's ×. The card is matched on the product URL both views
       link to — it carries the variant, so it identifies the exact saved item. */
    async removeFromDetail(button) {
      const details = button.parentElement.querySelector('.swym-more-details-btn');
      const href = details && details.getAttribute('href');

      if (!href) return;

      button.disabled = true;
      this.backToList();

      const card = await waitFor(() => {
        const links = this.swym && this.swym.querySelectorAll('.swym-wishlist-grid .swym-wishlist-image-wrapper');

        return links && Array.prototype.find.call(links, (link) => link.getAttribute('href') === href);
      }, 3000);

      const remove = card && card.closest('.swym-wishlist-item').querySelector('.swym-delete-btn');

      if (remove) remove.click();
    }
  }

  customElements.define('wishlist-page', WishlistPage);
})();
